import * as React from "react";
import PropTypes from "prop-types";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Reusable TablePagination component for consistent pagination across all tables.
 * 
 * @param {number} page - Current page number (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {number} total - Total number of entries
 * @param {number} pageSize - Number of items per page (default: 10)
 * @param {function} onPageChange - Callback when page changes: (newPage) => void
 * @param {string} className - Additional CSS classes for the container
 */
export function TablePagination({
  page,
  totalPages,
  total,
  pageSize = 10,
  onPageChange,
  className = "",
}) {
  const startEntry = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const endEntry = Math.min(page * pageSize, total);

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, 2);
      if (page > 4) pages.push("...");
      const start = Math.max(3, page - 1);
      const end = Math.min(totalPages - 2, page + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (page < totalPages - 3) pages.push("...");
      pages.push(totalPages - 1, totalPages);
    }
    return pages;
  };

  return (
    <div
      className={`px-4 lg:px-6 py-3 flex items-center justify-between border-t border-gray-100 ${className}`}
    >
      {/* Desktop: "Showing X to Y of Z entries" */}
      <span className="text-xs text-gray-500 hidden sm:inline">
        Showing {startEntry} to {endEntry} of {total} entries
      </span>
      {/* Mobile: "Z entries" */}
      <span className="text-xs text-gray-500 sm:hidden whitespace-nowrap">
        {total} entries
      </span>

      {/* Page buttons */}
      <div className="flex items-center gap-1">
        {/* Previous button */}
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="w-8 h-8 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-40 text-sm"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Page numbers */}
        {getPageNumbers().map((p, idx) => {
          const isDots = p === "...";
          const dotsPosition = idx > 3 ? "end" : "start";
          const key = isDots ? `dots-before-${dotsPosition}` : `page-${p}`;
          return isDots ? (
            <span
              key={key}
              className="w-8 h-8 flex items-center justify-center text-gray-400 text-sm"
            >
              ...
            </span>
          ) : (
            <button
              key={key}
              onClick={() => onPageChange(p)}
              className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium ${
                page === p
                  ? "bg-blue-600 text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              aria-label={`Page ${p}`}
              aria-current={page === p ? "page" : undefined}
            >
              {p}
            </button>
          );
        })}

        {/* Next button */}
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="w-8 h-8 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-40 text-sm"
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

TablePagination.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};

export default TablePagination;
